// Equitas Homes - 9-section Seller Intake schema (ported from
// sellerintake.equitashomes.com). Single source of truth used by the
// IntakeForm stepper and the IntakeDetails read-only summary.
//
// Field types:
//  - text, email, tel, textarea, date, number
//  - radio: pick ONE from options[]
//  - multi: pick MANY from options[]
//  - appliance_multi: special — multi-select with per-item brand + model inputs
//  - consent: required checkbox with inline label

export const INTAKE_SECTIONS = [
  {
    id: "seller_information",
    index: "01",
    title: "Seller Information",
    subtitle: "Where we start: the essentials so we can reach you.",
    fields: [
      { key: "seller_name", label: "Seller Name(s)", type: "text", required: true },
      { key: "property_address", label: "Property Address", type: "text", required: true },
      { key: "city_state_zip", label: "City, State and ZIP", type: "text", required: true },
      { key: "cell_phone", label: "Cell Phone", type: "tel", required: true },
      { key: "email", label: "Email", type: "email", required: true },
      { key: "additional_contact", label: "Additional Seller or Contact", type: "text" },
      { key: "secondary_seller_email", label: "Secondary Seller Email", type: "email" },
      {
        key: "preferred_contact_method", label: "Preferred Contact Method", type: "radio",
        options: ["Phone call", "Text", "Email"],
      },
      {
        key: "best_time_to_contact", label: "Best Time to Contact", type: "radio",
        options: ["Morning", "Afternoon", "Evening"],
      },
      { key: "tc_name", label: "Transaction Coordinator (TC) Name", type: "text", hint: "Added to escrow + daily digest once set." },
      { key: "tc_email", label: "Transaction Coordinator (TC) Email", type: "email" },
      {
        key: "daily_digest_enabled", type: "consent",
        label: "Enable the Daily Email Digest (sent each morning at 8:00 AM local time).",
      },
      {
        key: "daily_digest_recipients", label: "Digest Recipients", type: "multi",
        options: ["Agent", "Seller", "TC"],
        hint: "Each recipient receives the morning digest when enrolled.",
      },
    ],
  },
  {
    id: "selling_goals",
    index: "02",
    title: "Selling Goals",
    subtitle: "Your timeline and what a successful sale looks like for you.",
    fields: [
      {
        key: "reason_for_selling", label: "Reason for Selling", type: "radio",
        options: ["Upsizing", "Downsizing", "Relocating", "Retirement", "Investment", "Job transfer", "Divorce", "Estate sale", "Other"],
      },
      { key: "other_selling_reason", label: "Other Selling Reason", type: "text", showIf: { key: "reason_for_selling", equals: "Other" } },
      { key: "ideal_list_date", label: "Ideal List Date", type: "date", required: true, hint: "Anchors all 7-phase deadlines." },
      { key: "ideal_closing_date", label: "Ideal Closing Date", type: "date" },
      { key: "rent_back", label: "Will You Need a Rent-Back?", type: "radio", options: ["Yes", "No", "Unsure"] },
      { key: "purchased_another_home", label: "Have You Purchased Another Home?", type: "radio", options: ["Yes", "No", "Currently looking"] },
      {
        key: "preferred_timeline", label: "Preferred Selling Timeline", type: "radio",
        options: ["As soon as possible", "Within 30 days", "Within 60 days", "Within 90 days", "3-6 months", "More than 6 months", "Just exploring"],
      },
      {
        key: "top_priorities", label: "Top Priorities", type: "multi",
        options: ["Highest price", "Maximize net proceeds", "Quick sale", "Convenience", "Flexible closing", "Certainty of closing"],
      },
    ],
  },
  {
    id: "property_overview",
    index: "03",
    title: "Property Overview",
    subtitle: "The high-level picture of your home.",
    fields: [
      { key: "property_type", label: "Property Type", type: "radio", options: ["Single-family home", "Condominium", "Townhome", "Duplex", "Other"] },
      { key: "other_property_type", label: "Other Property Type", type: "text", showIf: { key: "property_type", equals: "Other" } },
      { key: "has_hoa", label: "Is There an HOA?", type: "radio", options: ["Yes", "No", "Unsure"] },
      { key: "monthly_hoa_fee", label: "HOA Fee Amount", type: "number", placeholder: "0", showIf: { key: "has_hoa", equals: "Yes" } },
      { key: "hoa_fee_frequency", label: "HOA Fee Frequency", type: "radio", options: ["Monthly", "Quarterly", "Semi-annually", "Annually", "Other"], showIf: { key: "has_hoa", equals: "Yes" } },
      {
        key: "hoa_includes", label: "What Does the HOA Include?", type: "multi",
        options: ["Pool", "Clubhouse", "Gym", "Tennis or pickleball", "Water", "Sewer", "Garbage", "Insurance", "Roof", "Exterior maintenance", "Landscaping", "Security", "Gated community", "Cable or internet"],
        showIf: { key: "has_hoa", equals: "Yes" },
      },
      { key: "other_hoa_info", label: "Other HOA Information", type: "text", showIf: { key: "has_hoa", equals: "Yes" } },
      {
        key: "home_facing_direction", label: "Home Facing Direction", type: "radio",
        options: ["North", "Northeast", "East", "Southeast", "South", "Southwest", "West", "Northwest", "Unsure"],
      },
      {
        key: "parking", label: "Parking", type: "multi",
        options: ["1-car garage", "2-car garage", "3+ car garage", "Detached garage", "Carport", "Assigned parking", "Covered parking", "Guest parking", "Street parking", "EV charger", "RV or boat parking", "None"],
      },
      { key: "parking_space_count", label: "Number of Assigned Parking Spaces (condo/townhome)", type: "number" },
      { key: "parking_space_numbers", label: "Assigned Parking Space Number(s)", type: "text", placeholder: "e.g., #142, #143" },
      { key: "storage_unit_number", label: "Assigned Storage Unit Number (if any)", type: "text" },
    ],
  },
  {
    id: "home_features",
    index: "04",
    title: "Home Features and Upgrades",
    subtitle: "The details that make your home shine.",
    fields: [
      { key: "kitchen_features", label: "Kitchen Features", type: "multi", options: ["Updated", "Quartz", "Granite", "Island", "Pantry"] },
      { key: "bathroom_features", label: "Bathroom Features", type: "multi", options: ["Updated", "Walk-in shower", "Soaking tub", "Double vanity"] },
      { key: "flooring", label: "Flooring", type: "multi", options: ["Hardwood", "Luxury vinyl plank", "Tile", "Carpet", "Laminate"] },
      {
        key: "home_features", label: "Home Features", type: "multi",
        options: ["Solar", "Smart home", "Dual-pane windows", "Fireplace", "Skylights", "Whole-house fan", "Central A/C", "Recessed lighting", "Water softener", "Security system", "EV charger", "Generator", "Crown molding", "Vaulted ceilings"],
      },
      { key: "outdoor_features", label: "Outdoor Features", type: "multi", options: ["Pool", "Spa", "Deck", "Patio", "Covered patio", "Firepit", "Outdoor kitchen", "Mature landscaping", "Fenced yard", "View"] },
    ],
  },
  {
    id: "appliances_items",
    index: "05",
    title: "Appliances and Items Included",
    subtitle: "What conveys with the home — plus brand and model for every appliance.",
    fields: [
      {
        key: "appliances",
        label: "Appliances Included",
        type: "appliance_multi",
        hint: "Toggle each appliance, then capture brand + model.",
        options: [
          { key: "refrigerator", label: "Refrigerator" },
          { key: "range_oven", label: "Range / Oven" },
          { key: "cooktop", label: "Cooktop" },
          { key: "microwave", label: "Microwave" },
          { key: "dishwasher", label: "Dishwasher" },
          { key: "washer", label: "Washer" },
          { key: "dryer", label: "Dryer" },
          { key: "wine_refrigerator", label: "Wine refrigerator" },
          { key: "garage_refrigerator", label: "Garage refrigerator" },
          { key: "water_softener", label: "Water softener" },
        ],
      },
      {
        key: "other_items_included", label: "Other Items Included", type: "multi",
        options: ["Window coverings", "Ceiling fans", "TV mounts", "Security cameras", "Smart doorbell", "Storage cabinets", "BBQ", "Patio furniture", "Hot tub"],
      },
      { key: "other_items_extra", label: "Other Items Included (Additional)", type: "text" },
      { key: "laundry_hookup", label: "Laundry Hookup", type: "radio", options: ["220V electric only", "Gas only", "Both 220V electric and gas", "110V only", "Unsure"] },
      { key: "range_oven_hookup", label: "Range / Oven Hookup", type: "radio", options: ["Gas", "Electric (220V)", "Electric (110V)", "Induction", "Dual fuel", "Unsure"] },
      { key: "other_220v_outlets", label: "Other 220V Outlets", type: "multi", options: ["Garage", "Workshop", "EV charger circuit", "Kitchen (double oven)", "None"] },
      { key: "items_excluded", label: "Items Excluded From the Sale", type: "textarea" },
    ],
  },
  {
    id: "access_security",
    index: "06",
    title: "Access and Security Information",
    subtitle: "So we can safely show and hand off the home.",
    privacyNotice: "Private — never shared on seller/collaborator links.",
    fields: [
      {
        key: "keys_available", label: "Keys and Access Items Available", type: "multi",
        options: ["Front door key", "Back door key", "Mailbox key", "Side gate key", "Garage door remotes", "Gate remote or fob", "Pool key or fob", "Clubhouse key or fob", "Storage unit key"],
      },
      { key: "garage_remote_count", label: "Number of Garage Remotes", type: "number" },
      { key: "other_keys", label: "Other Keys, Remotes or Fobs", type: "text" },
      { key: "front_door_code", label: "Front Door Code", type: "text", sensitive: true },
      { key: "garage_code", label: "Garage Code", type: "text", sensitive: true },
      { key: "gate_code", label: "Gate Code", type: "text", sensitive: true },
      { key: "alarm_code", label: "Alarm Code", type: "text", sensitive: true },
      { key: "other_access_codes", label: "Other Access Codes or Instructions", type: "text", sensitive: true },
      {
        key: "showing_instructions", label: "Preferred Showing Instructions", type: "multi",
        options: ["Supra lockbox", "Combination lockbox", "Appointment required", "Call first", "Text first"],
      },
      { key: "showing_restrictions", label: "Showing Restrictions or Special Instructions", type: "textarea" },
    ],
  },
  {
    id: "condition_disclosures",
    index: "07",
    title: "Property Condition and Disclosures",
    subtitle: "Full transparency protects you and buyers.",
    fields: [
      {
        key: "known_issues", label: "Known Issues or Conditions", type: "multi",
        options: ["Roof", "Plumbing", "Electrical", "Foundation", "Water damage", "Insurance claims", "Termite or pest", "Neighbor disputes", "Easements", "Permit issues", "None known"],
      },
      { key: "additional_condition_details", label: "Additional Details", type: "textarea", placeholder: "Repairs, leaks, disputes, etc." },
      { key: "inspection_reports", label: "Existing Inspection Reports or Warranties", type: "text" },
      { key: "permits_receipts", label: "Permits, Receipts and Improvement Records", type: "text" },
    ],
  },
  {
    id: "utilities_services",
    index: "08",
    title: "Utilities and Services",
    subtitle: "Who provides what — helpful for buyers and closing.",
    fields: [
      { key: "electric_provider", label: "Electric Provider", type: "text" },
      { key: "gas_provider", label: "Gas Provider", type: "text" },
      { key: "water_provider", label: "Water Provider", type: "text" },
      { key: "garbage_provider", label: "Garbage Provider", type: "text" },
      { key: "internet_provider", label: "Internet Provider", type: "text" },
      { key: "other_utility_provider", label: "Other Utility or Service Provider", type: "text" },
      { key: "owner_paid_utilities", label: "Utilities Paid Directly by the Owner", type: "multi", options: ["Electricity", "Gas", "Water", "Sewer", "Garbage", "Internet"] },
      { key: "utility_notes", label: "Utility, Solar or Service Notes", type: "textarea" },
    ],
  },
  {
    id: "marketing_presentation",
    index: "09",
    title: "Marketing and Presentation",
    subtitle: "How we'll bring the world to your front door.",
    fields: [
      { key: "preparation_openness", label: "Are You Open to Any of These Preparations?", type: "multi", options: ["Staging", "Painting", "Minor repairs", "Landscaping", "Decluttering", "Storage", "Professional cleaning"] },
      {
        key: "marketing_preferences", label: "Marketing Preferences", type: "multi",
        options: ["Professional photography", "Video", "Drone", "3D tour", "Floor plan", "Open houses", "Coming soon", "Social media advertising", "Broker tour", "Property website", "Mailers"],
      },
      { key: "marketing_requests", label: "Marketing Requests or Concerns", type: "textarea" },
      { key: "additional_info", label: "Additional Information or Questions", type: "text" },
      {
        key: "authorization", type: "consent", required: true,
        label: "I authorize Equitas Homes and Louis Fong to contact me regarding my property and potential real estate services.",
      },
    ],
  },
];

export function fieldVisible(field, values) {
  if (!field.showIf) return true;
  const v = values[field.showIf.key];
  if (field.showIf.equals !== undefined) return v === field.showIf.equals;
  return true;
}

function isFieldFilled(field, values) {
  const v = values[field.key];
  if (field.type === "consent") return !!v;
  if (field.type === "appliance_multi") {
    return !!v && typeof v === "object" && !Array.isArray(v) &&
      Object.values(v).some((row) => row && row.included);
  }
  if (Array.isArray(v)) return v.length > 0;
  return v !== undefined && v !== null && String(v).trim() !== "";
}

export function requiredFieldStats(values) {
  let total = 0, filled = 0;
  for (const s of INTAKE_SECTIONS) {
    for (const f of s.fields) {
      if (!f.required) continue;
      if (!fieldVisible(f, values)) continue;
      total += 1;
      if (isFieldFilled(f, values)) filled += 1;
    }
  }
  return { total, filled, pct: total ? Math.round((filled / total) * 100) : 0 };
}

// Per-section completeness used by the IntakeForm left-rail status badges.
// Returns {total, filled, remaining, hasRequired, complete} where hasRequired
// is false if the section has no required visible fields (so the UI can hide
// the badge entirely for optional sections).
export function sectionStats(section, values) {
  let total = 0, filled = 0;
  for (const f of section.fields) {
    if (!f.required) continue;
    if (!fieldVisible(f, values)) continue;
    total += 1;
    if (isFieldFilled(f, values)) filled += 1;
  }
  return {
    total,
    filled,
    remaining: Math.max(0, total - filled),
    hasRequired: total > 0,
    complete: total > 0 && filled === total,
  };
}

export function validateSection(section, values) {
  const errors = {};
  for (const f of section.fields) {
    if (!fieldVisible(f, values)) continue;
    if (!f.required) continue;
    const v = values[f.key];
    if (f.type === "consent") {
      if (!v) errors[f.key] = "Required to submit.";
      continue;
    }
    if (v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0)) {
      errors[f.key] = "This field is required.";
    }
  }
  return errors;
}
